import { FC } from 'react';
import type {
  AllDetailsFiltersState,
  AllDetailsSortState,
  DashboardBlockId,
  DashboardQualitySettings,
  HeatmapMode,
  QualityColumnDescriptor,
  QualityDetailItem,
  QualityDetailsStats,
  QualityHeatmapResponse,
} from '@/entities/dashboards';
import type { ConversationAttributeItem } from '@/entities/attributes';
import type { DashboardDrill } from '../model/useDashboardScreenStore';
import { HeatmapCard } from './HeatmapCard';
import { PeriodDetailsCard } from './PeriodDetailsCard';
import { CallDetailsCard } from './CallDetailsCard';
import { AllDetailsCard } from './AllDetailsCard';

/** Пропсы виджетов разбора: тепловая карта, drill-down по ячейке и таблица всех деталей. */
type Props = {
  /** Идентификаторы скрытых блоков дашборда (настройка отображения). */
  hiddenBlocks: DashboardBlockId[];
  heatmapData: QualityHeatmapResponse | undefined;
  heatmapLoading: boolean;
  heatmapMode: HeatmapMode;
  /** Выбранная ячейка heatmap для drill-down; `null` — разбор не активен. */
  drill: DashboardDrill;
  onSelectCell: (consultant: string, period: string) => void;
  /** Список звонков выбранной ячейки heatmap. */
  drillItems: QualityDetailItem[];
  /** Итоги всей ячейки: приходят тем же ответом, что и её список. */
  drillStats: QualityDetailsStats | null;
  blockNames: string[];
  drillLoading: boolean;
  selectedCallId: number | null;
  drillScoreFrom: string;
  drillScoreTo: string;
  onChangeScoreFrom: (v: string) => void;
  onChangeScoreTo: (v: string) => void;
  onResetScore: () => void;
  /** Выбор звонка для карточки оценки; `null` — закрыть карточку. */
  onSelectCall: (id: number | null) => void;
  /** Открытый звонок (карточка оценки); `null` — не открыт. */
  selectedCall: QualityDetailItem | null;
  /** Комментарии открытой оценки по идентификатору блока. */
  callComments: Map<number, string>;
  detailsItems: QualityDetailItem[];
  detailsLoading: boolean;
  availableColumns: QualityColumnDescriptor[];
  availableAttributes: ConversationAttributeItem[];
  allDetailsColumns: DashboardQualitySettings['allDetailsColumns'];
  onChangeAllDetailsColumns: (next: { visibleColumns: string[]; columnOrder: string[] }) => void;
  detailsSort: AllDetailsSortState | null;
  onSortChange: (next: AllDetailsSortState | null) => void;
  detailsFilters: AllDetailsFiltersState;
  onFiltersChange: (next: AllDetailsFiltersState) => void;
  /** Открытие одного диалога; `openInNewTab` — клик с shift. */
  onOpenConversation: (conversationId: number, openInNewTab: boolean) => void;
};

/**
 * Карточки разбора дашборда качества (Presenter): тепловая карта, drill-down по выбранной
 * ячейке (список звонков и карточка оценки одного звонка) и таблица всех деталей. Блок из
 * `hiddenBlocks` не рендерится.
 */
export const DashboardDetailsSection: FC<Props> = ({
  hiddenBlocks,
  heatmapData,
  heatmapLoading,
  heatmapMode,
  drill,
  onSelectCell,
  drillItems,
  drillStats,
  blockNames,
  drillLoading,
  selectedCallId,
  drillScoreFrom,
  drillScoreTo,
  onChangeScoreFrom,
  onChangeScoreTo,
  onResetScore,
  onSelectCall,
  selectedCall,
  callComments,
  detailsItems,
  detailsLoading,
  availableColumns,
  availableAttributes,
  allDetailsColumns,
  onChangeAllDetailsColumns,
  detailsSort,
  onSortChange,
  detailsFilters,
  onFiltersChange,
  onOpenConversation,
}) => {
  const isBlockVisible = (id: DashboardBlockId) => !hiddenBlocks.includes(id);

  return (
    <>
      {isBlockVisible('heatmap') && (
        <HeatmapCard
          data={heatmapData}
          isLoading={heatmapLoading}
          mode={heatmapMode}
          drill={drill}
          onSelectCell={onSelectCell}
        />
      )}

      <PeriodDetailsCard
        items={drillItems}
        stats={drillStats}
        blockNames={blockNames}
        isLoading={drillLoading}
        drill={drill}
        selectedCallId={selectedCallId}
        scoreFrom={drillScoreFrom}
        scoreTo={drillScoreTo}
        onChangeScoreFrom={onChangeScoreFrom}
        onChangeScoreTo={onChangeScoreTo}
        onResetScore={onResetScore}
        onSelectCall={onSelectCall}
      />

      <CallDetailsCard
        call={selectedCall}
        blockNames={blockNames}
        comments={callComments}
        onClose={() => onSelectCall(null)}
        onOpenConversation={onOpenConversation}
      />

      {isBlockVisible('allDetails') && (
        <AllDetailsCard
          items={detailsItems}
          blockNames={blockNames}
          isLoading={detailsLoading}
          availableColumns={availableColumns}
          availableAttributes={availableAttributes}
          columnSettings={allDetailsColumns}
          onChangeColumnSettings={onChangeAllDetailsColumns}
          sort={detailsSort}
          onSortChange={onSortChange}
          filters={detailsFilters}
          onFiltersChange={onFiltersChange}
          onOpenConversation={onOpenConversation}
        />
      )}
    </>
  );
};
